import { zipTieWireKey, type ZipTie } from '@erp/workflows';
import { isTextEntry } from 'src/lib/textFieldKeys';
import {
  classifyStroke,
  MIN_STROKE,
  strokeTargets,
  tieAtPoint,
  tieFromStroke,
  type EdgeGeometry,
  type Point,
  type StrokeTargets,
} from 'src/lib/workflows/zipTieGeometry';
import {
  beginZipPointer,
  zipKeyHeld,
  updateZipDrag,
  type ZipDrag,
  type ZipPointerBegin,
} from 'src/lib/workflows/zipTiePointer';
import type { ZipTieAction } from '../hooks/useWorkflowZipTies';

export type ZipTieGestureHost = {
  canvas: () => HTMLElement | null;
  readOnly: () => boolean;
  zoom: () => number;
  toFlow: (client: Point) => Point;
  ties: () => readonly ZipTie[];
  edges: () => EdgeGeometry[];
  hover: (id: string | null) => void;
  preview: (edgeIds: string[], cutTieIds: string[]) => void;
  gesture: (ties: ZipTie[] | null) => void;
  commit: (ties: ZipTie[], action: ZipTieAction) => void;
  menu: (tie: ZipTie, client: Point) => void;
  stroke: (
    line: { start: Point; end: Point; cutting: boolean; tying: boolean } | null,
  ) => void;
};

type Cut = {
  pointerId: number;
  start: Point;
  targets: StrokeTargets;
  /** Last preview sent, so an unchanged hit set doesn't re-render every edge. */
  previewKey: string;
};

function isControl(target: EventTarget | null): boolean {
  return (
    target instanceof Element &&
    Boolean(
      target.closest('button, a, input, textarea, select, [role="button"]'),
    )
  );
}

function inCanvas(
  target: EventTarget | null,
  canvas: HTMLElement | null,
): boolean {
  return target instanceof Node && canvas !== null && canvas.contains(target);
}

function claimablePress(
  event: PointerEvent,
  canvas: HTMLElement | null,
): boolean {
  return (
    event.button === 0 &&
    inCanvas(event.target, canvas) &&
    !isControl(event.target)
  );
}

function strokeLongEnough(start: Point, end: Point, zoom: number): boolean {
  return (
    Math.hypot(end.x - start.x, end.y - start.y) >= MIN_STROKE / (zoom || 1)
  );
}

/**
 * Window-level listeners for the zip-tie gestures. Capture phase runs before
 * React Flow's pane, so a claimed stroke or strap drag doesn't also pan the
 * canvas. Returns the unsubscribe.
 */
export function installZipTieGestures(host: ZipTieGestureHost): () => void {
  let keyHeld = false;
  let drag: ZipDrag | null = null;
  let cut: Cut | null = null;
  const lastDown = { id: null as string | null, time: 0 };
  const resetDoubleClick = () => {
    lastDown.id = null;
    lastDown.time = 0;
  };

  const onKey = (event: KeyboardEvent) => {
    keyHeld = zipKeyHeld(keyHeld, event, isTextEntry(event.target));
  };

  const clearStroke = () => {
    cut = null;
    host.preview([], []);
    host.stroke(null);
  };

  const commitCut = (stroke: Cut, end: Point) => {
    if (!strokeLongEnough(stroke.start, end, host.zoom())) return;
    const ties = host.ties();
    const { cutTieIds, hits } = classifyStroke(
      stroke.targets,
      stroke.start,
      end,
    );
    if (cutTieIds.length > 0) {
      const removed = new Set(cutTieIds);
      host.commit(
        ties.filter((tie) => !removed.has(tie.id)),
        'remove',
      );
      return;
    }
    const created = tieFromStroke(hits, ties, () => crypto.randomUUID());
    if (created) host.commit([...ties, created], 'bundle');
  };

  const finishCut = (event: PointerEvent) => {
    const stroke = cut;
    clearStroke();
    if (!stroke || event.type !== 'pointerup' || host.readOnly()) return;
    commitCut(stroke, host.toFlow({ x: event.clientX, y: event.clientY }));
  };

  const dragAction = (current: ZipDrag): ZipTieAction | null => {
    if (!current.moved || host.readOnly()) return null;
    return current.rotate ? 'rotate' : 'move';
  };

  const commitOrCancelDrag = (current: ZipDrag) => {
    const action = dragAction(current);
    if (!action) {
      host.gesture(null);
      return;
    }
    resetDoubleClick();
    host.commit(current.ties, action);
  };

  const finishDrag = () => {
    const current = drag;
    drag = null;
    if (!current) return;
    commitOrCancelDrag(current);
  };

  const openMenu = (event: MouseEvent) => {
    const tie = tieAtPoint(
      host.ties(),
      host.toFlow({ x: event.clientX, y: event.clientY }),
      host.zoom(),
    );
    if (!tie) return;
    event.preventDefault();
    event.stopPropagation();
    host.menu(tie, { x: event.clientX, y: event.clientY });
  };

  const onContextMenu = (event: MouseEvent) => {
    const canvas = host.canvas();
    if (
      !inCanvas(event.target, canvas) ||
      host.readOnly() ||
      isControl(event.target)
    ) {
      return;
    }
    openMenu(event);
  };

  const beginFromEvent = (event: PointerEvent): ZipPointerBegin => {
    const point = host.toFlow({ x: event.clientX, y: event.clientY });
    return beginZipPointer({
      readOnly: host.readOnly(),
      keyHeld,
      modifiers: {
        shift: event.shiftKey,
        alt: event.altKey,
        ctrl: event.ctrlKey,
        meta: event.metaKey,
      },
      tie: tieAtPoint(host.ties(), point, host.zoom()) ?? null,
      point,
      ties: host.ties(),
      pointerId: event.pointerId,
      lastDown,
      now: performance.now(),
    });
  };

  const startCut = (event: PointerEvent, point: Point) => {
    cut = {
      pointerId: event.pointerId,
      start: point,
      targets: strokeTargets(host.edges(), host.ties()),
      previewKey: '',
    };
    host.preview([], []);
    host.stroke({ start: point, end: point, cutting: false, tying: false });
  };

  const startRemove = (tieId: string) => {
    resetDoubleClick();
    host.commit(
      host.ties().filter((item) => item.id !== tieId),
      'remove',
    );
  };

  const claimBegin = (event: PointerEvent, began: ZipPointerBegin) => {
    if (began.kind === 'ignore') return;
    event.preventDefault();
    event.stopPropagation();
    if (began.kind === 'cut') {
      startCut(event, began.point);
      return;
    }
    if (began.kind === 'remove') {
      startRemove(began.tieId);
      return;
    }
    lastDown.id = began.drag.tieId;
    lastDown.time = began.now;
    drag = began.drag;
  };

  const onPointerDown = (event: PointerEvent) => {
    if (!claimablePress(event, host.canvas())) return;
    claimBegin(event, beginFromEvent(event));
  };

  const followCut = (event: PointerEvent): boolean => {
    if (!cut || event.pointerId !== cut.pointerId) return false;
    event.stopPropagation();
    const last = host.toFlow({ x: event.clientX, y: event.clientY });
    const { cutTieIds, hits } = classifyStroke(cut.targets, cut.start, last);
    const wireKeys = hits.map((hit) => zipTieWireKey(hit.wire));
    const previewKey = `${wireKeys.join('|')}#${cutTieIds.join('|')}`;
    if (previewKey !== cut.previewKey) {
      cut.previewKey = previewKey;
      host.preview(wireKeys, cutTieIds);
    }
    host.stroke({
      start: cut.start,
      end: last,
      cutting: cutTieIds.length > 0,
      tying: hits.length > 0,
    });
    return true;
  };

  const followDrag = (event: PointerEvent): boolean => {
    if (!drag || event.pointerId !== drag.pointerId) return false;
    event.stopPropagation();
    drag = updateZipDrag(
      drag,
      host.toFlow({ x: event.clientX, y: event.clientY }),
      !event.altKey,
    );
    host.gesture(drag.ties);
    return true;
  };

  const hoverAt = (event: PointerEvent) => {
    if (!inCanvas(event.target, host.canvas()) || event.buttons !== 0) return;
    const tie = tieAtPoint(
      host.ties(),
      host.toFlow({ x: event.clientX, y: event.clientY }),
      host.zoom(),
    );
    host.hover(tie?.id ?? null);
  };

  const onPointerMove = (event: PointerEvent) => {
    if (followCut(event)) return;
    if (followDrag(event)) return;
    hoverAt(event);
  };

  const releaseCut = (event: PointerEvent): boolean => {
    if (!cut || event.pointerId !== cut.pointerId) return false;
    event.stopPropagation();
    finishCut(event);
    return true;
  };

  const releaseDrag = (event: PointerEvent) => {
    if (!drag || event.pointerId !== drag.pointerId) return;
    event.stopPropagation();
    finishDrag();
  };

  const onPointerUp = (event: PointerEvent) => {
    if (releaseCut(event)) return;
    releaseDrag(event);
  };

  const onBlur = () => {
    keyHeld = false;
    if (cut) clearStroke();
    if (drag) finishDrag();
  };

  window.addEventListener('contextmenu', onContextMenu, true);
  window.addEventListener('keydown', onKey, true);
  window.addEventListener('keyup', onKey, true);
  window.addEventListener('pointerdown', onPointerDown, true);
  window.addEventListener('pointermove', onPointerMove, true);
  window.addEventListener('pointerup', onPointerUp, true);
  window.addEventListener('pointercancel', onPointerUp, true);
  window.addEventListener('blur', onBlur);
  return () => {
    window.removeEventListener('contextmenu', onContextMenu, true);
    window.removeEventListener('keydown', onKey, true);
    window.removeEventListener('keyup', onKey, true);
    window.removeEventListener('pointerdown', onPointerDown, true);
    window.removeEventListener('pointermove', onPointerMove, true);
    window.removeEventListener('pointerup', onPointerUp, true);
    window.removeEventListener('pointercancel', onPointerUp, true);
    window.removeEventListener('blur', onBlur);
  };
}
